import { Component } from '@theme/component';

const COUNTRIES = new Set(('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' '));

/** Verified records use only a UTC calendar date; expire conservatively from that day's start. */
export function recentPurchases(feed, product, maxAge = 72, now = Date.now()) {
  if (feed?.verified !== true || !Array.isArray(feed.purchases)) return [];
  const cutoff = now - Math.min(72, Math.max(1, Number(maxAge) || 72)) * 3600000;
  const seen = new Set();
  return feed.purchases.filter(record => {
    const at = Date.parse(record?.date);
    const key = `${record?.date}:${record?.country}:${record?.quantity}`;
    if (record?.status !== 'paid' || record?.test === true || record?.cancelled === true || record?.product !== product ||
      !COUNTRIES.has(record?.country) || !Number.isInteger(record?.quantity) || record.quantity < 1 || record.quantity > 100 ||
      !/^\d{4}-\d{2}-\d{2}$/.test(record?.date || '') || !Number.isFinite(at) ||
      new Date(at).toISOString().slice(0, 10) !== record.date || at > now || at < cutoff || seen.has(key)) return false;
    try {
      if (new Intl.DisplayNames(['en'], { type:'region' }).of(record.country) === record.country) return false;
    } catch { return false; }
    seen.add(key);
    return true;
  }).sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
}

/** Native flag fallback for countries without a bundled flag image. */
export function countryFlag(country) {
  if (!COUNTRIES.has(country)) return '';
  return String.fromCodePoint(...[...country].map(letter => letter.charCodeAt(0) + 127397));
}

/** @extends {Component<{card:HTMLElement,buyer:HTMLElement,flag:HTMLElement,action:HTMLElement,progress:HTMLElement}>} */
class RecentPurchases extends Component {
  requiredRefs = ['card', 'buyer', 'flag', 'action', 'progress'];
  #abort;
  #observer;
  #resize;
  #timer;
  #progress;
  #motion;
  #config;
  #feed;
  #current;
  #state = 'idle';
  #count = 0;
  #index = 0;
  #due = 0;
  #remaining = 0;
  #pointer = false;
  #key = 'hm-recent-preorders-v1';

  connectedCallback() { super.connectedCallback(); this.#start(); }
  updatedCallback() { super.updatedCallback(); this.#cleanup(); this.#start(); }
  disconnectedCallback() { this.#cleanup(); super.disconnectedCallback(); }

  #start() {
    this.refs.card.hidden = true;
    try { this.#config = JSON.parse(this.querySelector('[data-activity-config]').textContent); } catch { return; }
    const c = this.#config;
    this.#key = c.sessionKey || 'hm-recent-preorders-v1';
    if (!c.feed || !c.product || /\/(checkouts?|payments?|thank_you)(\/|$)/.test(location.pathname) ||
      (!c.mobile && matchMedia('(max-width:749px)').matches)) return;
    this.#abort = new AbortController();
    const options = { signal:this.#abort.signal };
    this.#motion = matchMedia('(prefers-reduced-motion:reduce)');
    const saved = this.#read();
    this.#count = saved.count || 0;
    this.#index = this.#count;
    this.#pointer = false;
    this.#remaining = 0;
    if (saved.dismissed || this.#count >= this.#limit()) return;
    document.addEventListener('visibilitychange', () => this.#reconcile(), options);
    document.addEventListener('visitorConsentCollected', () => this.#reconcile(), options);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.refs.card.hidden) this.dismiss();
    }, options);
    for (const event of ['focusin', 'focusout', 'play', 'pause', 'ended']) {
      document.addEventListener(event, () => queueMicrotask(() => this.#reconcile()), { ...options, capture:true });
    }
    this.refs.card.addEventListener('pointerenter', () => { this.#pointer = true; this.#reconcile(); }, options);
    this.refs.card.addEventListener('pointerleave', () => { this.#pointer = false; this.#reconcile(); }, options);
    this.#motion.addEventListener('change', () => this.#reconcile(), options);
    this.#observer = new MutationObserver(() => this.#reconcile());
    this.#observer.observe(document.body, { subtree:true, childList:true, attributes:true, attributeFilter:['open','hidden','aria-modal'] });
    const header = document.querySelector('#header-group');
    if (header) {
      this.#resize = new ResizeObserver(() => this.#position(header));
      this.#resize.observe(header);
      this.#position(header);
    }
    this.#state = 'waiting';
    const controller = this.#abort;
    // Fetch once after the primary content has rendered. No order API or personal data in the browser.
    this.#timer = setTimeout(async () => {
      try {
        const response = await fetch(c.feed, { signal:controller.signal, credentials:'omit', cache:'no-cache' });
        if (!response.ok) throw new Error('Unavailable feed');
        const feed = await response.json();
        if (!this.isConnected || controller.signal.aborted || this.#abort !== controller) return;
        this.#feed = feed;
        this.#due = Date.now();
        this.#reconcile();
      } catch { if (this.isConnected && this.#abort === controller && !controller.signal.aborted) this.#finish(); }
    }, Math.max(0, Number(c.delay) || 0) * 1000);
  }

  #position(header) {
    this.style.setProperty('--hm-activity-offset', `${Math.min(200, Math.max(0, header.getBoundingClientRect().height))}px`);
  }

  #limit() { return this.#motion?.matches ? 1 : Math.max(1, Math.min(5, Number(this.#config.limit) || 3)); }

  #obstructed() {
    if (document.hidden) return true;
    if ([...document.querySelectorAll('dialog[open],[aria-modal="true"],theme-drawer[open],#shopify-pc__banner,#shopify-pc__prefs,#header-group details[open]')]
      .some(node => !this.contains(node) && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden')) return true;
    const focused = document.activeElement;
    if (!this.contains(focused) && focused?.matches('input,textarea,select,[contenteditable="true"]')) return true;
    return [...document.querySelectorAll('video')].some(video => !video.paused && !video.ended && video.getClientRects().length);
  }

  #reconcile() {
    if (!this.#feed || this.#state === 'done' || !this.isConnected) return;
    if (this.#obstructed()) {
      clearTimeout(this.#timer);
      if (this.#state === 'showing') {
        this.#remaining = Math.max(500, this.#due - Date.now());
        this.#progress?.pause();
        this.refs.card.hidden = true;
        this.#state = 'suspended';
      }
      return;
    }
    if (this.#state === 'suspended') {
      if (!recentPurchases(this.#feed, this.#config.product, this.#config.maxAge).includes(this.#current)) return this.#finish();
      this.#render(this.#current);
      this.refs.card.hidden = false;
      this.#state = 'showing';
      this.#due = Date.now() + this.#remaining;
      this.#progress?.play();
    }
    if (this.#state === 'showing') {
      const pause = this.#pointer || this.contains(document.activeElement);
      if (pause && !this.#remaining) {
        this.#remaining = Math.max(500, this.#due - Date.now());
        clearTimeout(this.#timer);
        this.#progress?.pause();
      } else if (!pause) {
        if (this.#remaining) {
          this.#due = Date.now() + this.#remaining;
          this.#remaining = 0;
          this.#progress?.play();
        }
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.#hide(), Math.max(0, this.#due - Date.now()));
      }
      return;
    }
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#show(), Math.max(0, this.#due - Date.now()));
  }

  #render(record) {
    const c = this.#config;
    const country = new Intl.DisplayNames([c.locale || 'en'], { type:'region' }).of(record.country);
    this.refs.buyer.textContent = c.buyer.replace('[country]', country);
    this.refs.flag.replaceChildren();
    if (c.flags?.[record.country]) {
      // Bundled vectors keep flags visible on Windows, where native flag emoji may show country letters.
      const image = document.createElement('img');
      image.src = c.flags[record.country];
      image.alt = '';
      image.width = 20;
      image.height = 15;
      this.refs.flag.append(image);
    } else this.refs.flag.textContent = countryFlag(record.country);
    this.refs.action.textContent = record.quantity > 1 ? c.multiple.replace('[quantity]', String(record.quantity)) : c.action;
  }

  #show() {
    if (this.#obstructed()) return;
    const records = recentPurchases(this.#feed, this.#config.product, this.#config.maxAge);
    if (!records.length || this.#count >= this.#limit() || this.#index >= records.length) return this.#finish();
    this.#current = records[this.#index++];
    this.#render(this.#current);
    this.#count++;
    this.#write({ count:this.#count });
    this.#state = 'showing';
    this.#remaining = 0;
    this.refs.card.hidden = false;
    const duration = Math.max(4000, Number(this.#config.duration) * 1000 || 4500);
    this.#due = Date.now() + duration;
    if (!this.#motion.matches && this.refs.card.animate) {
      const enter = this.refs.card.animate([{ opacity:0, transform:'translateY(-12px) scale(.97)' },{ opacity:1, transform:'translateY(0) scale(1)' }],
        { duration:360, easing:'cubic-bezier(.2,.85,.25,1.1)' });
      enter.finished.catch(() => {});
      this.#progress?.cancel();
      this.#progress = this.refs.progress.animate([{ transform:'scaleX(1)' },{ transform:'scaleX(0)' }], { duration, fill:'forwards' });
      this.#progress.finished.catch(() => {});
    }
    this.#reconcile();
  }

  #hide() {
    if (this.#pointer || this.contains(document.activeElement)) return this.#reconcile();
    if (!this.#motion.matches && this.refs.card.animate) {
      const exit = this.refs.card.animate([{ opacity:1, transform:'translateY(0)' },{ opacity:0, transform:'translateY(-7px)' }], { duration:170, fill:'forwards' });
      exit.finished.then(() => { exit.cancel(); if (this.isConnected && this.#state === 'gap') this.refs.card.hidden = true; }).catch(() => {});
    } else this.refs.card.hidden = true;
    this.#state = 'gap';
    this.#due = Date.now() + Math.max(1000, Number(this.#config.gap) * 1000 || 1500);
    this.#reconcile();
  }

  /** User dismissal hides all remaining notifications for this browser session. */
  dismiss() {
    this.#write({ count:this.#count, dismissed:true });
    this.#finish();
  }

  #read() {
    try {
      if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true) {
        return JSON.parse(sessionStorage.getItem(this.#key) || '{}');
      }
    } catch { /* Storage denied: retain page-local limits only. */ }
    return {};
  }

  #write(value) {
    try {
      if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true) sessionStorage.setItem(this.#key, JSON.stringify(value));
    } catch { /* No tracking or identifying data is needed. */ }
  }

  #finish() { this.#state = 'done'; this.#cleanup(); }
  #cleanup() {
    clearTimeout(this.#timer);
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#resize?.disconnect();
    this.#progress?.cancel();
    this.refs.card?.getAnimations?.().forEach(animation => animation.cancel());
    if (this.refs.card) this.refs.card.hidden = true;
    this.#feed = undefined;
    this.#current = undefined;
  }
}

if (!customElements.get('hovermist-recent-purchases')) customElements.define('hovermist-recent-purchases', RecentPurchases);
